import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowDown } from "@phosphor-icons/react";
import useReducedMotion from "../../../../lib/useReducedMotion";
import "./hero.scss";

gsap.registerPlugin(ScrollTrigger);

// The original four-line kinetic title, preserved verbatim.
const LINES = [
  "Delaina Fernandes",
  "Industrial Designer",
  "Visual Designer",
  "3D Visualizer",
];

// Split into characters so each can be staggered independently. Spaces keep
// their width via white-space: pre on the span. data-drift is the character's
// signed distance from the centre of its own line, which is the direction it
// travels when the line breathes open.
const chars = (line) => {
  const mid = (line.length - 1) / 2;
  return line.split("").map((ch, i) => (
    <span className="hero__char" data-drift={i - mid} key={`${ch}-${i}`}>
      {ch}
    </span>
  ));
};

const Hero = () => {
  const sceneRef = useRef(null);
  const stageRef = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const scene = sceneRef.current;
    const stage = stageRef.current;
    if (!scene || !stage) return;

    const ctx = gsap.context(() => {
      // The original anime.js beats, matched: scale to 0.5, open the tracking,
      // scale back, close the tracking. The spacing step is expressed as a
      // per-character x offset rather than letterSpacing, so it never reflows
      // the line: a 4px multiplier on the signed distance from a line's centre
      // reproduces the original 6px -> 10px tracking exactly.
      // grid:"auto" lets GSAP infer rows and columns from the laid-out
      // positions, which is what makes it radiate from the centre across four
      // lines of different lengths.
      const title = gsap.utils.toArray(".hero__char");
      const stagger = { amount: 1, grid: "auto", from: "center" };

      // The original opened tracking from 6px to 10px on a 40px face, i.e. by
      // 0.1em per gap. Deriving the offset from each line's own font size keeps
      // that proportion at this type scale and at every breakpoint, instead of
      // a fixed pixel value that reads as barely-there on the large name.
      const spread = (i, el) =>
        Number(el.dataset.drift) *
        parseFloat(getComputedStyle(el).fontSize) *
        0.1;

      // Big -> scale small -> go wide -> get big -> normal tracking.
      gsap
        .timeline({ repeat: -1, defaults: { ease: "sine.inOut" } })
        .to(title, { scale: 0.5, duration: 1.1, stagger })
        .to(title, { x: spread, duration: 1.1, stagger })
        .to(title, { scale: 1, duration: 1.1, stagger })
        .to(title, { x: 0, duration: 1.1, stagger });

      // Entry, once.
      gsap.from(title, {
        opacity: 0,
        yPercent: 60,
        rotateX: -70,
        duration: 1,
        ease: "power3.out",
        stagger: { amount: 0.5, grid: "auto", from: "center" },
      });

      // Hands off to the next section: the title recedes and dims rather than
      // simply scrolling away.
      gsap.to(stage, {
        yPercent: -12,
        opacity: 0.15,
        ease: "none",
        scrollTrigger: {
          trigger: scene,
          start: "top top",
          end: "bottom top",
          scrub: 0.6,
        },
      });

      return () => {};
    }, sceneRef);

    return () => ctx.revert();
  }, [reduced]);

  return (
    <section className="hero" ref={sceneRef} aria-labelledby="hero-name">
      <div className="hero__stage" ref={stageRef}>
        <h1 className="hero__title" id="hero-name" aria-label={LINES[0]}>
          <span className="hero__line hero__line--name" aria-hidden="true">
            {chars(LINES[0])}
          </span>
        </h1>

        <p className="hero__roles">
          <span className="u-visually-hidden">
            {LINES.slice(1).join(". ")}.
          </span>
          {LINES.slice(1).map((line) => (
            <span className="hero__line" key={line} aria-hidden="true">
              {chars(line)}
            </span>
          ))}
        </p>

        <a className="hero__cta" href="#work">
          Selected work
          <ArrowDown size={16} weight="bold" />
        </a>
      </div>
    </section>
  );
};

export default Hero;
